import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Pencil, Trash2, AlertTriangle, Save, ExternalLink, Check, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND_COLORS, formatPrice } from "@/lib/menuData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Concept tabs matching OrderPage
const CONCEPT_TABS = [
  { id: "chicken", label: "Chicken", color: BRAND_COLORS.sunsetOrange },
  { id: "pizza", label: "Pizza", color: "#10B981" },
  { id: "lunch", label: "Lunch", color: "#8B5CF6" },
  { id: "burgers", label: "Burgers", color: "#EF4444" },
];

function MenuEditorCard({ item, onToggle, onPriceChange, onDelete, accentColor }) {
  const [editing, setEditing] = useState(false);
  const [priceInput, setPriceInput] = useState(item.price?.toString() || "0");
  const [saving, setSaving] = useState(false);

  const handlePriceSave = async () => {
    const newPrice = parseFloat(priceInput);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error("Invalid price");
      return;
    }
    setSaving(true);
    const success = await onPriceChange(item.id, newPrice);
    setSaving(false);
    if (success) {
      setEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePriceSave();
    } else if (e.key === "Escape") {
      setPriceInput(item.price?.toString() || "0");
      setEditing(false);
    }
  };

  return (
    <div
      data-testid={`editor-card-${item.id}`}
      className={`relative bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
        item.available !== false ? "border-slate-100" : "border-red-300"
      }`}
    >
      {/* Unavailable Overlay */}
      {item.available === false && (
        <div className="absolute inset-0 bg-red-500/20 z-10 flex items-center justify-center pointer-events-none">
          <Badge className="bg-red-600 text-white text-xs font-bold px-3 py-1">
            UNAVAILABLE
          </Badge>
        </div>
      )}

      {/* Draft Badge */}
      {item.published === false && (
        <div className="absolute top-2 left-2 z-20">
          <Badge className="bg-amber-500 text-white text-[10px]">DRAFT</Badge>
        </div>
      )}

      {/* Controls - top right */}
      <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
        <Switch
          data-testid={`toggle-${item.id}`}
          checked={item.available !== false}
          onCheckedChange={() => onToggle(item.id, item.available !== false)}
          className="scale-75"
        />
        <button
          data-testid={`delete-${item.id}`}
          className="w-7 h-7 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
          onClick={() => onDelete(item)}
        >
          <Trash2 className="h-3.5 w-3.5 text-red-600" />
        </button>
      </div>

      {/* Image */}
      <div className="h-28 bg-slate-100 overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-2xl">🍗</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <p className="font-bold text-sm truncate" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
          {item.name}
        </p>
        <p className="text-xs text-slate-500 truncate mt-0.5">
          {item.size || item.category || "No category"}
        </p>

        {/* Price - Editable */}
        <div className="mt-2 flex items-center justify-between">
          {editing ? (
            <div className="flex items-center gap-1 flex-1">
              <span className="text-sm font-bold" style={{ color: accentColor }}>$</span>
              <Input
                data-testid={`price-input-${item.id}`}
                type="number"
                step="0.01"
                min="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handlePriceSave}
                autoFocus
                className="h-7 w-20 text-sm font-bold px-1"
                style={{ color: accentColor }}
              />
              <button
                className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center hover:bg-green-200"
                onClick={handlePriceSave}
                disabled={saving}
              >
                <Check className="h-3.5 w-3.5 text-green-600" />
              </button>
              <button
                className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"
                onClick={() => {
                  setPriceInput(item.price?.toString() || "0");
                  setEditing(false);
                }}
              >
                <X className="h-3.5 w-3.5 text-slate-600" />
              </button>
            </div>
          ) : (
            <button
              data-testid={`price-edit-${item.id}`}
              className="flex items-center gap-1 group"
              onClick={() => setEditing(true)}
            >
              <span className="font-extrabold text-base" style={{ color: accentColor }}>
                {formatPrice(item.price)}
              </span>
              <Pencil className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminMenuEditor() {
  const navigate = useNavigate();
  const { token, role, getAuthHeaders, logout } = useAuth();
  const [concept, setConcept] = useState("chicken");
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Auth check
  useEffect(() => {
    if (!token || role !== "admin") {
      navigate("/admin/login");
    }
  }, [token, role, navigate]);

  const fetchMenu = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [menuRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/menu`, { headers }),
        axios.get(`${API}/admin/categories`, { headers }),
      ]);
      const items = menuRes.data.items || menuRes.data;
      setMenuItems(Array.isArray(items) ? items : []);
      setCategories(catsRes.data || []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load menu");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, logout, navigate]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  const getItemsByConcept = (conceptId) => {
    return menuItems.filter((item) => item.concept === conceptId);
  };

  const getItemsByCategory = (cat) => {
    return menuItems.filter((item) => item.category === cat);
  };

  const handleToggle = async (itemId, currentAvailable) => {
    try {
      const headers = getAuthHeaders();
      const item = menuItems.find((i) => i.id === itemId);
      if (!item) return;

      await axios.put(
        `${API}/admin/menu/${itemId}`,
        { ...item, available: !currentAvailable },
        { headers }
      );

      setMenuItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, available: !currentAvailable } : i))
      );
      toast.success(`${item.name} ${!currentAvailable ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to toggle item");
    }
  };

  const handlePriceChange = async (itemId, newPrice) => {
    try {
      const headers = getAuthHeaders();
      const item = menuItems.find((i) => i.id === itemId);
      if (!item) return false;

      await axios.put(
        `${API}/admin/menu/${itemId}`,
        { ...item, price: newPrice },
        { headers }
      );

      setMenuItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, price: newPrice } : i))
      );
      toast.success(`Price updated to ${formatPrice(newPrice)}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update price");
      return false;
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/menu/${deleteDialog.id}`, { headers });
      setMenuItems((prev) => prev.filter((i) => i.id !== deleteDialog.id));
      toast.success(`${deleteDialog.name} deleted`);
      setDeleteDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  const currentTab = CONCEPT_TABS.find((t) => t.id === concept);
  const conceptItems = getItemsByConcept(concept);

  // Group items by category
  const conceptCategories = categories.filter((c) => c.concept === concept);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
        <div className="animate-spin h-8 w-8 border-4 border-slate-200 border-t-orange-500 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.sandWhite }} data-testid="admin-menu-editor">
      {/* Header */}
      <header className="h-14 flex items-center justify-between px-4 md:px-6" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 h-9 px-2"
            onClick={() => navigate("/admin")}
            data-testid="back-to-admin-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-extrabold text-white" style={{ fontFamily: "Montserrat" }}>
            Admin Menu Editor
          </h1>
          <Badge className="text-[10px]" style={{ backgroundColor: BRAND_COLORS.sunsetOrange, color: "white" }}>
            ADMIN VIEW
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-white/30 text-white hover:bg-white/10"
          onClick={() => window.open("/order/chicken", "_blank")}
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Customer View
        </Button>
      </header>

      {/* Concept Tabs */}
      <div className="sticky top-0 z-30 border-b px-4 py-2" style={{ backgroundColor: "white" }}>
        <div className="max-w-5xl mx-auto">
          <Tabs value={concept} onValueChange={setConcept}>
            <TabsList className="h-10 bg-slate-100 p-1 rounded-xl">
              {CONCEPT_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  data-testid={`tab-${tab.id}`}
                  className="rounded-lg px-4 text-xs font-semibold data-[state=active]:shadow-sm transition-all"
                  style={{
                    ...(concept === tab.id && { backgroundColor: tab.color, color: "white" }),
                  }}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Info Banner */}
      <div className="max-w-5xl mx-auto px-4 py-3">
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            <strong>Admin View:</strong> Edit prices inline, toggle availability, or delete items. Changes save immediately.
          </p>
        </div>
      </div>

      {/* Menu Content */}
      <main className="max-w-5xl mx-auto px-4 pb-8">
        {conceptItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No items found for this concept</p>
          </div>
        ) : conceptCategories.length > 0 ? (
          // Render by category
          conceptCategories.map((cat) => {
            const catItems = getItemsByCategory(cat.key || cat.id);
            if (catItems.length === 0) return null;
            return (
              <div key={cat.key || cat.id} className="mb-8">
                <div className="flex items-center gap-2 mb-3 mt-4">
                  <div className="w-1 h-5 rounded-full" style={{ backgroundColor: currentTab?.color }} />
                  <h3 className="text-base font-bold" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                    {cat.name || cat.label}
                  </h3>
                  <Badge variant="outline" className="text-[10px]">{catItems.length} items</Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {catItems.map((item) => (
                    <MenuEditorCard
                      key={item.id}
                      item={item}
                      onToggle={handleToggle}
                      onPriceChange={handlePriceChange}
                      onDelete={setDeleteDialog}
                      accentColor={currentTab?.color || BRAND_COLORS.sunsetOrange}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Render all items without category grouping
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
            {conceptItems.map((item) => (
              <MenuEditorCard
                key={item.id}
                item={item}
                onToggle={handleToggle}
                onPriceChange={handlePriceChange}
                onDelete={setDeleteDialog}
                accentColor={currentTab?.color || BRAND_COLORS.sunsetOrange}
              />
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Menu Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
